import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  useTheme,
  IconButton,
  Grid,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, parseISO, subDays } from 'date-fns';
import { motion } from 'framer-motion';
import { useDailySales } from '../hooks/useSalesData';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ArrowUpward,
  ArrowDownward,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import StatsCard from '../components/StatsCard';
import LuxuryTable from '../components/LuxuryTable';
import { glassStyle, animationVariants } from '../theme/luxuryTheme';

const ComparisonCard: React.FC<{
  title: string;
  current: number;
  previous: number;
  change: number;
  changePercent: number;
  prefix?: string;
  delay?: number;
}> = ({ title, current, previous, change, changePercent, prefix = '$', delay = 0 }) => {
  const theme = useTheme();
  const isPositive = change >= 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ y: -5 }}
    >
      <Card
        sx={{
          ...glassStyle,
          p: 3,
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: '0 8px 32px rgba(175, 35, 38, 0.2)',
            border: '1px solid rgba(175, 35, 38, 0.2)',
          },
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {title}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 1 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontFamily: '"Space Grotesk", monospace',
              background: isPositive
                ? 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)'
                : 'linear-gradient(135deg, #FF4D4F 0%, #FF6B6D 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {prefix}{current.toLocaleString()}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isPositive ? (
              <ArrowUpward sx={{ color: theme.palette.success.main, fontSize: 20 }} />
            ) : (
              <ArrowDownward sx={{ color: theme.palette.error.main, fontSize: 20 }} />
            )}
            <Typography
              variant="body1"
              sx={{
                color: isPositive ? theme.palette.success.main : theme.palette.error.main,
                fontWeight: 600,
              }}
            >
              {Math.abs(changePercent).toFixed(1)}%
            </Typography>
          </Box>
        </Box>
        
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Previous: {prefix}{previous.toLocaleString()}
        </Typography>
        
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: isPositive ? theme.palette.success.main : theme.palette.error.main,
            boxShadow: isPositive
              ? '0 0 10px rgba(82, 196, 26, 0.5)'
              : '0 0 10px rgba(255, 77, 79, 0.5)',
          }}
        />
      </Card>
    </motion.div>
  );
};

const DailySales: React.FC = () => {
  const theme = useTheme();
  const [selectedDate, setSelectedDate] = useState<Date | null>(subDays(new Date(), 1));
  
  const formattedDate = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : undefined;
  const { data, isLoading, error } = useDailySales(formattedDate);

  const orderColumns = [
    { id: 'orderNumber', label: 'Order #' },
    { id: 'time', label: 'Time', format: (value: string) => value },
    { id: 'subtotal', label: 'Subtotal', align: 'right' as const, format: (value: number) => `$${value.toFixed(2)}` },
    { id: 'tax', label: 'Tax', align: 'right' as const, format: (value: number) => `$${value.toFixed(2)}` },
    { id: 'total', label: 'Total', align: 'right' as const, format: (value: number) => `$${value.toFixed(2)}` },
    { id: 'status', label: 'Status' },
  ];

  // Orders data is not currently available from the backend
  // This is placeholder for when order-level data becomes available
  const orderData = data?.current?.orders && Array.isArray(data.current.orders) && data.current.orders.length > 0
    ? data.current.orders.slice(0, 10).map(order => ({
        orderNumber: order.orderNumber,
        time: order.businessDate ? format(parseISO(order.businessDate), 'h:mm a') : 'N/A',
        subtotal: order.subtotal,
        tax: order.tax,
        total: order.total,
        status: 'Completed',
      }))
    : [];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error loading sales data</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <motion.div {...animationVariants.fadeIn}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Daily Sales Report
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Detailed breakdown of your daily performance
            </Typography>
          </Box>
          <Box
            sx={{
              ...glassStyle,
              p: 1,
              borderRadius: '12px',
            }}
          >
            <DatePicker
              label="Select Date"
              value={selectedDate}
              onChange={(newValue) => setSelectedDate(newValue)}
              format="MM/dd/yyyy"
              slots={{
                openPickerIcon: CalendarIcon,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'transparent',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                  },
                },
              }}
            />
          </Box>
        </Box>
      </motion.div>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={6}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.1 }}>
            <Card
              sx={{
                ...glassStyle,
                background: 'linear-gradient(135deg, rgba(175, 35, 38, 0.15) 0%, rgba(175, 35, 38, 0.05) 100%)',
                border: '1px solid rgba(175, 35, 38, 0.2)',
                p: 4,
                height: '100%',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Total Revenue
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      fontFamily: '"Space Grotesk", monospace',
                      background: 'linear-gradient(135deg, #af2326 0%, #c94144 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 2,
                    }}
                  >
                    ${data?.current?.totalSales?.toLocaleString() || '0'}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {format(selectedDate || new Date(), 'MMMM dd, yyyy')}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '20px',
                    background: 'linear-gradient(135deg, #3F76FF 0%, #5A8BFF 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 32px rgba(175, 35, 38, 0.3)',
                  }}
                >
                  <CalendarIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>
              </Box>
            </Card>
          </motion.div>
        </Grid>
        
        <Grid item xs={12} lg={6}>
          <Grid container spacing={2} sx={{ height: '100%' }}>
            <Grid item xs={6}>
              <StatsCard
                title="Total Orders"
                value={data?.current?.orderCount || 0}
                change={data?.comparisons?.yearOverYear?.ordersPercent}
                icon={<TrendingUpIcon />}
                delay={0.2}
                color="secondary"
              />
            </Grid>
            <Grid item xs={6}>
              <StatsCard
                title="Average Order"
                value={data?.current?.averageOrderValue || 0}
                prefix="$"
                icon={<TrendingUpIcon />}
                delay={0.3}
                color="success"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Period Comparisons */}
      <motion.div {...animationVariants.fadeIn} transition={{ delay: 0.4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Period Comparisons
        </Typography>
      </motion.div>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <ComparisonCard
            title="Day over Day"
            current={data?.current?.totalSales || 0}
            previous={(data?.current?.totalSales || 0) - (data?.comparisons?.dayOverDay?.sales || 0)}
            change={data?.comparisons?.dayOverDay?.sales || 0}
            changePercent={data?.comparisons?.dayOverDay?.salesPercent || 0}
            delay={0.5}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ComparisonCard
            title="Week over Week"
            current={data?.current?.totalSales || 0}
            previous={(data?.current?.totalSales || 0) - (data?.comparisons?.weekOverWeek?.sales || 0)}
            change={data?.comparisons?.weekOverWeek?.sales || 0}
            changePercent={data?.comparisons?.weekOverWeek?.salesPercent || 0}
            delay={0.6}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ComparisonCard
            title="Year over Year"
            current={data?.current?.totalSales || 0}
            previous={data?.comparisons?.yearOverYear?.lastYearData?.totalSales || 0}
            change={data?.comparisons?.yearOverYear?.sales || 0}
            changePercent={data?.comparisons?.yearOverYear?.salesPercent || 0}
            delay={0.7}
          />
        </Grid>
      </Grid>

      {/* Peak Hour Card */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.8 }}>
            <Card
              sx={{
                ...glassStyle,
                p: 3,
                background: 'linear-gradient(135deg, rgba(82, 196, 26, 0.1) 0%, rgba(82, 196, 26, 0.05) 100%)',
                border: '1px solid rgba(82, 196, 26, 0.2)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '16px',
                      background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <TimeIcon sx={{ fontSize: 30, color: 'white' }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Peak Hour Performance
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      12:00 PM - 1:00 PM
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#52c41a' }}>
                    $850
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    in sales during peak hour
                  </Typography>
                </Box>
              </Box>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Orders Table */}
      <motion.div {...animationVariants.fadeIn} transition={{ delay: 0.9 }}>
        <LuxuryTable
          title="Recent Orders"
          subtitle={data?.current?.orders && Array.isArray(data.current.orders) && data.current.orders.length > 10 
            ? `Showing 10 of ${data.current.orders.length} orders`
            : orderData.length > 0 
              ? 'All orders for the selected date'
              : 'Order details not available'
          }
          columns={orderColumns}
          data={orderData}
        />
      </motion.div>
    </Box>
  );
};

export default DailySales;